import cx from 'classnames';
import { secondsToMilliseconds } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonVariants } from '@components/button/Button';
import { ExternalRefundLink } from '@components/externalRefundLink/ExternalRefundLink';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import typography from '~styles/typography.scss';
import { useRestaurant } from '../../context/useRestaurant';
import { refund } from '../../reservations/apiHelpers';
import styles from './RefundConfirmationModal.scss';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { useForm } from 'react-hook-form';

export interface RefundConfirmationModalProps {
  // The historical default for this prop was true, but disabling the portal
  // caused the modal to be visually scoped to the container in the Occupant
  // Details Drawer. However, we found that removing the prop breaks the
  // functionality in the existing callsites (guest book, unassigned)
  disablePortal?: boolean;
  handleAfterConfirm?: () => void;
  isOpen: boolean;
  reservationId: string;
  closeModal: () => void;
}

const CURRENT_PAGE = 0;
const SUCCESS_MESSAGE =
  "Success: The guest's reservation has been successfully refunded";
const REFUND_REASON_PLACEHOLDER =
  'Please enter the reason for refunding this reservation';

type FormValues = {
  refundReason: string;
};
export const RefundConfirmationModal = ({
  disablePortal = true,
  handleAfterConfirm,
  isOpen,
  reservationId,
  closeModal,
}: RefundConfirmationModalProps) => {
  const { id: restaurantId } = useRestaurant();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      refundReason: '',
    },
  });
  const refreshPage = () =>
    setTimeout(() => navigate(CURRENT_PAGE), secondsToMilliseconds(3));

  const handleRefund = (data: FormValues) => {
    const trimmedRefundReason = data.refundReason.trim();
    if (!trimmedRefundReason) return;

    setIsLoading(true);

    void (async () => {
      try {
        await refund(restaurantId, reservationId, trimmedRefundReason);
        successToast({ message: SUCCESS_MESSAGE });
      } catch (error) {
        if (error instanceof ApiError) {
          errorToast({
            message: `Failed to refund reservation: ${error.message}`,
          });
        } else {
          errorToast({
            message: 'Failed to refund reservation. Please try again.',
          });
        }
      }
      if (handleAfterConfirm) {
        handleAfterConfirm();
      } else {
        refreshPage();
      }
    })();
  };
  return (
    <Modal
      ariaLabel="Refund"
      isOpen={isOpen}
      onClose={closeModal}
      disablePortal={disablePortal}
    >
      <form onSubmit={handleSubmit((data) => handleRefund(data))}>
        <p className={cx(typography.c2, styles.message)}>
          Are you sure you would like to refund this reservation? Please
          reference the Peak <ExternalRefundLink className={styles.link} /> for
          any concerns regarding refunds.
        </p>
        <ControlledFormTextareaAutosize
          control={control}
          label="Refund reason"
          name="refundReason"
          placeholder={REFUND_REASON_PLACEHOLDER}
        />
        <ModalActions>
          <Button
            label="Go Back"
            onClick={closeModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            isDisabled={isLoading || !watch('refundReason').trim()}
            label="Confirm"
            type="submit"
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </form>
    </Modal>
  );
};
